/**
 * useUpdates — real-time update hook using long-polling.
 *
 * Calls the `getUpdates` JSON-RPC method in a loop (with noWait=false for blocking).
 * When updates arrive, invalidates the TanStack Query cache for studies
 * so the grid auto-refreshes.
 *
 * Mount once at the AppShell level.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/rpc';
import { queryKeys } from '../api/queryKeys';

/** How long to wait before retrying after an error */
const ERROR_RETRY_MS = 5_000;

/**
 * Long-poll for real-time study updates.
 * Automatically invalidates TanStack query cache when updates arrive.
 */
export function useUpdates(enabled = true) {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    async function poll() {
      while (!cancelled) {
        try {
          const controller = new AbortController();
          abortRef.current = controller;

          // Long-poll — server holds the connection until updates are available
          // or a timeout occurs (typically ~30s)
          const result = await api.getUpdates(false);

          if (cancelled) break;

          // If we got any update records, invalidate the studies cache
          if (result && result.records && result.records.length > 0) {
            queryClient.invalidateQueries({ queryKey: queryKeys.studies.all });
          }
        } catch (err: unknown) {
          if (cancelled) break;

          // On error, wait before retrying to avoid tight loops
          const errMsg = err instanceof Error ? err.message : String(err);
          // Don't log abort errors (expected during cleanup)
          if (!errMsg.includes('abort') && !errMsg.includes('cancel')) {
            console.warn('[useUpdates] poll error, retrying in 5s:', errMsg);
          }
          await new Promise((r) => setTimeout(r, ERROR_RETRY_MS));
        }
      }
    }

    poll();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [enabled, queryClient]);
}
